import { Download, Loader2, Plus, Upload, X } from "lucide-react";
import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useAuth } from "../../context/AuthContext";
import {
  createEmptyBulkUserDraft,
  formatBulkUserValidationErrors,
  getBulkUserCsvTemplate,
  parseBulkUserCsv,
  validateBulkUserDrafts,
  type BulkUserDraft,
  type BulkUserInput,
} from "./bulk-user-schema";

import { GradeSelect } from "./GradeSelect";
import { bulkUserServerErrors } from "./users-config-api";

type BulkCreateUsersDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateUsers: (users: BulkUserInput[]) => Promise<boolean>;
  isCreating: boolean;
};

const hasAnyFieldValue = (draft: BulkUserDraft): boolean =>
  Object.values(draft).some((value) => value.trim().length > 0);

export const BulkCreateUsersDialog = ({
  isOpen,
  onOpenChange,
  onCreateUsers,
  isCreating,
}: BulkCreateUsersDialogProps) => {
  const canPlace = useAuth().allowedTabs?.includes("classrooms") ?? false;
  const [activeTab, setActiveTab] = useState<"csv" | "manual">("csv");
  const [csvFileName, setCsvFileName] = useState("");
  const [csvDrafts, setCsvDrafts] = useState<BulkUserDraft[]>([]);
  const [csvRowNumbers, setCsvRowNumbers] = useState<number[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [manualRows, setManualRows] = useState<BulkUserDraft[]>([
    createEmptyBulkUserDraft(),
  ]);
  const [manualErrors, setManualErrors] = useState<string[]>([]);

  const csvSummaryText = useMemo(() => {
    if (csvDrafts.length === 0) return null;
    return `${csvDrafts.length} users ready to import.`;
  }, [csvDrafts.length]);

  const resetState = () => {
    setActiveTab("csv");
    setCsvFileName("");
    setCsvDrafts([]);
    setCsvRowNumbers([]);
    setCsvErrors([]);
    setManualRows([createEmptyBulkUserDraft()]);
    setManualErrors([]);
  };

  const handleDialogOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      resetState();
    }
  };

  const handleTabChange = (value: string) => {
    if (value !== "csv" && value !== "manual") return;
    setActiveTab(value);
  };

  const handleDownloadTemplate = () => {
    const template = getBulkUserCsvTemplate();
    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = "bulk-user-template.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvFileUpload = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { setCsvDrafts([]); setCsvErrors(["Upload a CSV file."]); return; }

    try {
      const content = await file.text();
      const parsedCsv = parseBulkUserCsv(content);
      setCsvFileName(file.name);

      const validation = validateBulkUserDrafts(parsedCsv.drafts, 2, parsedCsv.rowNumbers);
      const errors = [...parsedCsv.errors, ...formatBulkUserValidationErrors(validation.errors)];
      if (parsedCsv.drafts.length > 100) errors.push("Import at most 100 users at a time.");
      if (errors.length > 0) {
        setCsvDrafts([]);
        setCsvRowNumbers([]);
        setCsvErrors(errors);
        return;
      }

      setCsvDrafts(parsedCsv.drafts);
      setCsvRowNumbers(parsedCsv.rowNumbers ?? parsedCsv.drafts.map((_, index) => index + 2));
      setCsvErrors([]);
      toast.success(`Loaded ${parsedCsv.drafts.length} users from CSV.`);
    } catch (error) {
      console.error(error);
      setCsvDrafts([]);
      setCsvErrors(["Unable to read CSV file."]);
    }
  };

  const updateManualRow = (
    index: number,
    field: keyof BulkUserDraft,
    value: string,
  ) => {
    setManualRows((previousRows) =>
      previousRows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        // Switching away from STUDENT clears any grade already chosen, so a
        // stale value can't linger hidden and fail validation on submit.
        const clearsGrade = field === "account_role" && value !== "STUDENT";
        return {
          ...row,
          [field]: value,
          ...(clearsGrade ? { grade: "" } : {}),
        };
      }),
    );
  };

  const addManualRow = () => {
    setManualRows((previousRows) => previousRows.length < 100 ? [...previousRows, createEmptyBulkUserDraft()] : previousRows);
  };

  const removeManualRow = (index: number) => {
    setManualRows((previousRows) => {
      const nextRows = previousRows.filter((_, rowIndex) => rowIndex !== index);
      return nextRows.length > 0 ? nextRows : [createEmptyBulkUserDraft()];
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (activeTab === "csv") {
      if (csvDrafts.length === 0) {
        setCsvErrors(["Upload a CSV file that passes validation first."]);
        return;
      }

      const validation = validateBulkUserDrafts(csvDrafts, 2, csvRowNumbers);
      if (validation.errors.length > 0) {
        setCsvErrors(formatBulkUserValidationErrors(validation.errors));
        return;
      }

      setCsvErrors([]);
      try {
        const shouldClose = await onCreateUsers(validation.users);
        if (shouldClose) handleDialogOpenChange(false);
      } catch (error) {
        setCsvErrors(bulkUserServerErrors(error, csvRowNumbers));
      }
      return;
    }

    const sourceRows = manualRows.map((draft, index) => ({ draft, row: index + 1 })).filter(({ draft }) => hasAnyFieldValue(draft));
    const nonEmptyRows = sourceRows.map(({ draft }) => draft);
    const rowNumbers = sourceRows.map(({ row }) => row);
    if (nonEmptyRows.length === 0) {
      setManualErrors(["Enter at least one user before submitting."]);
      return;
    }

    const validation = validateBulkUserDrafts(nonEmptyRows, 1, rowNumbers);
    if (validation.errors.length > 0) {
      setManualErrors(formatBulkUserValidationErrors(validation.errors));
      return;
    }

    setManualErrors([]);
    try {
      const shouldClose = await onCreateUsers(validation.users);
      if (shouldClose) handleDialogOpenChange(false);
    } catch (error) {
      setManualErrors(bulkUserServerErrors(error, rowNumbers));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Bulk Create Users</DialogTitle>
          <DialogDescription>
            Create multiple users at once with strict schema validation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="csv">CSV Upload</TabsTrigger>
              <TabsTrigger value="manual">Multi-entry Form</TabsTrigger>
            </TabsList>

            <TabsContent value="csv" className="space-y-4">
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <p className="text-sm text-foreground">
                  Use the template so headers and roles match the strict schema.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  account_role is STUDENT or TEACHER. Organization accepts an exact name or ID.
                  Students require an organization.
                  For students, grade and classroom are optional. Separate classroom names with | or ;.
                  {!canPlace && " Placing students in classrooms requires the Classrooms grant; leave classroom empty without it."}
                  Quote CSV cells containing commas. All rows must pass validation before any user is created.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  className="mt-3 gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Template
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-users-csv">Upload CSV</Label>
                <Input
                  id="bulk-users-csv"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileUpload}
                />
                {csvFileName && (
                  <p className="text-xs text-muted-foreground">
                    Loaded file: {csvFileName}
                  </p>
                )}
                {csvSummaryText && (
                  <p className="text-xs text-lime-700">{csvSummaryText}</p>
                )}
              </div>

              {csvErrors.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
                  <p className="text-sm font-medium text-destructive">
                    Fix the following errors:
                  </p>
                  <ul role="alert" className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-destructive">
                    {csvErrors.map((error, index) => (
                      <li key={`${index}-${error}`}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="manual" className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Choose Student or Teacher. Organization accepts an exact name or ID.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addManualRow}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </Button>
              </div>

              <ScrollArea className="h-[360px] rounded-md border border-border/70 p-3">
                <div className="space-y-3 pr-3">
                  {manualRows.map((row, index) => (
                    <div
                      key={`manual-row-${index}`}
                      className="rounded-md border border-border/70 p-3"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                          Entry {index + 1}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => removeManualRow(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        <Input
                          placeholder="Name (optional)"
                          value={row.name}
                          onChange={(event) =>
                            updateManualRow(index, "name", event.target.value)
                          }
                        />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={row.email}
                          onChange={(event) =>
                            updateManualRow(index, "email", event.target.value)
                          }
                        />
                        <Input
                          type="password"
                          placeholder="Password"
                          value={row.password}
                          onChange={(event) =>
                            updateManualRow(
                              index,
                              "password",
                              event.target.value,
                            )
                          }
                        />
                        <Input
                          placeholder="Username (optional)"
                          value={row.username}
                          onChange={(event) =>
                            updateManualRow(
                              index,
                              "username",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <Select
                          value={row.account_role}
                          onValueChange={(value) =>
                            updateManualRow(index, "account_role", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Account role" />
                          </SelectTrigger>
                          <SelectContent>
                            {(["STUDENT", "TEACHER"] as const).map((role) => (
                              <SelectItem key={role} value={role}>
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input className="mt-2" aria-label={`Organization for entry ${index + 1}`}
                        placeholder={row.account_role === "STUDENT" ? "Organization name or ID (required)" : "Organization name or ID (optional)"} value={row.organization}
                        onChange={(event) => updateManualRow(index, "organization", event.target.value)} />
                      {row.account_role === "STUDENT" && (
                        <div className="mt-2">
                          <Input className="mb-2" aria-label={`Classrooms for entry ${index + 1}`} disabled={!canPlace}
                            placeholder={canPlace ? "Classroom names (optional, | separated)" : "Classroom placement requires the Classrooms grant"} value={row.classroom}
                            onChange={(event) => updateManualRow(index, "classroom", event.target.value)} />
                          <GradeSelect
                            value={row.grade}
                            onChange={(value) =>
                              updateManualRow(index, "grade", value)
                            }
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {manualErrors.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
                  <p className="text-sm font-medium text-destructive">
                    Fix the following errors:
                  </p>
                  <ul role="alert" className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-destructive">
                    {manualErrors.map((error, index) => (
                      <li key={`${index}-${error}`}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || (activeTab === "csv" && csvDrafts.length === 0)} className="gap-2">
              {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
              <Upload className="h-4 w-4" />
              {isCreating ? "Creating..." : "Create Users"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
